import { useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ModalTitle from "../../../../components/HexModal/ModalTitle";
import HexAuthButton from "../../../../components/AuthButton";

import { getLabel } from "../../../../hooks/use-labels";

import {
  setCartInformation,
  updateCart,
} from "../../../../redux/reducers/order/order-action";

import ProductSwiper from "../ProductSwiper";
import SuccessfulCartView from "./SuccessfulCartView";
import PriceView from "./PriceView";

import {
  getOtherProductsQuantity,
  getProductQuantity,
  isDifferentSupplier,
  newSupplierCartRequest,
  prepareOtherProductCart,
  prepareUpdateCartRequest,
  updateStorageOnClearCart,
} from "./product-cart-detail-utils";

import DetailView from "./DetailView";

import HexLoader from "../../../../components/HexLoader";
import { getErrorCode, handleError } from "../../../../utils/common-utils";
import Notification from "../../../../components/Notification";
import FlexButtons from "../../../../components/FlexButtons";

import ProductNotificationModals from "../../Components/ProductNotificationModals";
import { getConfig } from "../../../../hooks/use-config";

let timeoutRef = null;

const ProductDetails = ({ product, material }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { inStock } = product;

  const { carts } = useSelector((state) => state.order);

  const [otherProductsQuantity, setOtherProductsQuantity] = useState(0);

  const [productQuantity, setProductQuantity] = useState(
    getProductQuantity(carts, product, material)
  );

  const [notificationModal, showNotificationModal] = useState(false);
  const [successView, showSuccessView] = useState(false);

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState("");
  const [invalidProduct, setInvalidProduct] = useState(false);
  const [buyNow, setBuyNow] = useState(false);

  const MAX_PRODUCTS = +getConfig("maxCartItems");
  const MAX_UNIQUE_PRODUCT = +getConfig("maxUniqueCartItems");

  useEffect(() => {
    setOtherQuantity(carts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveToCart = () => {
    navigate("/my-cart");
  };

  const setOtherQuantity = (supplierCart) => {
    const otherQuantity = getOtherProductsQuantity(
      supplierCart,
      product.id,
      material.id
    );
    setOtherProductsQuantity(otherQuantity);
  };

  const updateCartDetails = (quantity, moveToCartCB) => {
    setLoading(true);
    timeoutRef = setTimeout(() => {
      const details = prepareUpdateCartRequest(
        carts,
        product,
        quantity,
        material.id
      );

      updateCart(details)
        .then(({ data }) => {
          dispatch(setCartInformation(data.carts[0]));
          setOtherQuantity(data.carts[0]);

          clearTimeout(timeoutRef);
          timeoutRef = null;
          setLoading(false);

          moveToCartCB && moveToCartCB();
        })
        .catch((error) => {
          const code = getErrorCode(error);
          let message = handleError(error);

          if (code.includes("invalid.product")) {
            message = "productUnavailableLabel";
            setInvalidProduct(true);
          }

          setNotification(message);
          setLoading(false);
        });
    }, 500);
  };

  const toggleModal = (type) => {
    showNotificationModal(type ? { type } : false);
  };

  const validateCart = (quantity) => {
    let valid = true;
    const totalQuantity = quantity + otherProductsQuantity;
    const hasCart = !_.isEmpty(carts);

    const diffentSupplier =
      hasCart && isDifferentSupplier(carts, product.supplier.id);

    const details = hasCart
      ? prepareOtherProductCart(carts, product.id, material.id)
      : [];

    if (diffentSupplier) {
      toggleModal("otherSupplier");
      valid = false;
    } else if (totalQuantity > MAX_PRODUCTS) {
      toggleModal("cartLimit");
      valid = false;
    } else if (details.length >= MAX_UNIQUE_PRODUCT) {
      toggleModal("uniqueProductLimit");
      valid = false;
    }

    return valid;
  };

  const onAddInCart = () => {
    buyNow && setBuyNow(false);
    const quantity = productQuantity + 1;
    const valid = validateCart(quantity);

    if (valid) {
      setProductQuantity(quantity);

      if (timeoutRef) {
        clearTimeout(timeoutRef);
        timeoutRef = null;
        showSuccessView(true);
        updateCartDetails(quantity);
      } else {
        showSuccessView(true);
        updateCartDetails(quantity);
      }
    }
  };

  const onBuyNow = () => {
    setBuyNow(true);

    const quantity = productQuantity + 1;
    const valid = validateCart(quantity);

    if (valid) {
      updateCartDetails(quantity, moveToCart);
    }
  };

  const onEmptyCart = () => {
    const details = newSupplierCartRequest({
      product,
      materialId: material.id,
    });

    toggleModal();
    setLoading(true);

    updateCart(details).then(({ data }) => {
      dispatch(setCartInformation(data.carts[0]));
      updateStorageOnClearCart(false);
      setOtherProductsQuantity(0);
      setProductQuantity(1);
      setLoading(false);
      buyNow ? moveToCart() : showSuccessView(true);
    });
  };

  const renderModal = () => {
    if (notificationModal) {
      return (
        <ProductNotificationModals
          type={notificationModal.type}
          onClose={() => toggleModal()}
          onEmptyCart={onEmptyCart}
        />
      );
    }

    return null;
  };

  const disabled = notification || invalidProduct || !inStock;

  return (
    <div className="product-detail-modal">
      {renderModal()}
      <ModalTitle text={product.name} className={"capitalize"} />

      <ProductSwiper
        images={product.imageUrls}
        className={disabled && "outOfStockProduct"}
      />

      <DetailView product={product} materialName={material.label} />

      <PriceView {...product} />

      {loading ? (
        <HexLoader />
      ) : (
        successView &&
        !notification && <SuccessfulCartView moveToCart={moveToCart} />
      )}

      {notification && <Notification type={"danger"} message={notification} />}

      <FlexButtons>
        <HexAuthButton
          label={
            inStock
              ? getLabel("addToCartLabel")
              : !inStock && getLabel("outOfStockLabel")
          }
          disable={disabled}
          buttonType={"outline"}
          onClick={onAddInCart}
          buttonProps={{ size: "md" }}
          className={"add-to-cart-button full-width"}
        />

        {inStock && (
          <HexAuthButton
            disable={disabled}
            onClick={onBuyNow}
            buttonProps={{ size: "md" }}
            label={getLabel("buyNowLabel")}
            className={"add-to-cart-button full-width"}
          />
        )}
      </FlexButtons>
    </div>
  );
};

export default ProductDetails;
